import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as ScrollLink } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

function Navbar() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navItems = [
    { name: t('nav.home'), to: 'hero' },
    { name: t('nav.services'), to: 'services' },
    { name: t('nav.fleet'), to: 'fleet' },
    { name: t('nav.routes'), to: 'routes' },
    { name: t('nav.whyUs'), to: 'why-us' },
    { name: t('nav.contact'), to: 'contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={isScrolled ? 4 : 0}
        sx={{
          backgroundColor: isScrolled ? 'primary.main' : 'transparent',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Typography
              component={ScrollLink}
              to="hero"
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
              sx={{
                flexGrow: 1,
                color: 'secondary.main',
                fontWeight: 700,
                letterSpacing: 1,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  transition: 'opacity 0.3s ease',
                },
              }}
            >
              LUX ROYAL TRAVEL
            </Typography>

            {isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LanguageSelector />
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ color: 'secondary.main', ml: 1 }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.name}
                    component={ScrollLink}
                    to={item.to}
                    spy={true}
                    smooth={true}
                    offset={-70}
                    duration={500}
                    sx={{
                      color: 'secondary.main',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'secondary.light',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease',
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
                <LanguageSelector />
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
            backgroundColor: 'primary.main',
          },
        }}
      >
        <Box sx={{ mt: 8 }}>
          <List>
            {navItems.map((item) => (
              <ListItem 
                key={item.name}
                component={ScrollLink}
                to={item.to}
                spy={true}
                smooth={true}
                offset={-70}
                duration={500}
                onClick={handleDrawerToggle}
                button
                sx={{
                  color: 'secondary.main',
                  '&:hover': {
                    backgroundColor: 'rgba(197, 164, 126, 0.08)',
                  },
                }}
              >
                <ListItemText 
                  primary={item.name}
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: 500,
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
}

export default Navbar; 